"use client";

import { useEffect } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useSession, signOut } from "next-auth/react";
import { useI18n } from "@/app/components/LanguageProvider";
import { useLocale } from "@/app/components/LocaleProvider";
import { getBaseLocale, getPathWithLocale } from "@/lib/i18n";
import { cn } from "@/lib/utils";

interface SideMenuProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  onLogin: () => void;
}

export default function SideMenu({ open, onToggle, onClose, onLogin }: SideMenuProps) {
  const navbarT = useTranslations("Navbar");
  const commonT = useTranslations("Common");
  const { toggle } = useI18n();
  const locale = useLocale();
  const pathname = usePathname();
  const { data: session } = useSession();

  const isEnglish = getBaseLocale(locale) === "en";

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  const navItems = [
    { href: getPathWithLocale("/about", locale), labelKey: "about" },
    { href: getPathWithLocale("/services", locale), labelKey: "services" },
    { href: getPathWithLocale("/industries", locale), labelKey: "industries" },
    { href: getPathWithLocale("/technologies", locale), labelKey: "technologies" },
    { href: getPathWithLocale("/articles", locale), labelKey: "articles" },
    { href: getPathWithLocale("/contacts", locale), labelKey: "contacts" },
    ...(session?.user?.role === "admin" ? [{ href: "/admin", labelKey: "admin" }] : []),
  ];

  const dashboardHref =
    session?.user?.role === "admin"
      ? "/admin"
      : getPathWithLocale("/dashboard", locale);

  return (
    <>
      {/* Вертикальная кнопка «Меню» слева (только десктоп) */}
      <div className="hidden md:block">
        <Link
          href={getPathWithLocale("/", locale)}
          className="menu-open__logo"
          aria-label="Dev48"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/images/logo-full-white.svg" alt="Dev48" width={28} height={28} />
        </Link>
        <button
          className="menu-open"
          type="button"
          onClick={onToggle}
          aria-expanded={open}
        >
          <div className="menu-open__text">{navbarT("menu")}</div>
          <div className="menu-open__icon">
            <div className="menu-open__icon-top" />
            <div className="menu-open__icon-center" />
            <div className="menu-open__icon-bottom" />
          </div>
        </button>
      </div>

      {/* Подложка */}
      <div
        className="menu-backdrop"
        onClick={onClose}
        aria-hidden={!open}
        style={{
          opacity: open ? 1 : 0,
          visibility: open ? "visible" : "hidden",
          pointerEvents: open ? "auto" : "none",
          transition: open
            ? "opacity 0.3s ease"
            : "opacity 0.3s ease, visibility 0s linear 0.3s",
        }}
      />

      {/* Колонка меню */}
      <aside
        className="menu"
        aria-label={navbarT("menu")}
        aria-hidden={!open}
        style={{
          clipPath: open ? "inset(0 0% 0 0)" : "inset(0 100% 0 0)",
          visibility: open ? "visible" : "hidden",
          transition: open
            ? "clip-path 0.45s cubic-bezier(0.5, 0, 0, 1)"
            : "clip-path 0.45s cubic-bezier(0.5, 0, 0, 1), visibility 0s linear 0.45s",
        }}
      >
            {/* Шапка: крестик */}
            <div className="menu__header">
              <button
                type="button"
                className="menu-close"
                onClick={onClose}
                aria-label={navbarT("close")}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="menu__body">
              {/* Язык (только мобайл) */}
              <div className="menu__lang md:hidden">
                <span className="menu__lang-label">{isEnglish ? "Language" : "ЯЗЫК"}</span>
                <div className="menu__lang-btns">
                  <button
                    type="button"
                    onClick={() => {
                      if (locale !== "ru") toggle();
                    }}
                    className={cn(
                      "menu__lang-btn",
                      locale === "ru" && "menu__lang-btn--active",
                    )}
                  >
                    RU
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (locale !== "en") toggle();
                    }}
                    className={cn(
                      "menu__lang-btn",
                      locale === "en" && "menu__lang-btn--active",
                    )}
                  >
                    EN
                  </button>
                </div>
              </div>

              {/* Навигация */}
              <ul className="menu__list">
                {navItems.map((item) => (
                  <li key={item.href} className="menu__list-point">
                    <Link
                      href={item.href}
                      onClick={onClose}
                      className={cn(
                        "menu__list-link",
                        pathname === item.href && "active",
                      )}
                    >
                      {navbarT(item.labelKey as never)}
                    </Link>
                  </li>
                ))}
              </ul>

              {/* Авторизация + CTA (только мобайл) */}
              <div className="menu__auth md:hidden">
                {session?.user ? (
                  <>
                    <Link
                      href={dashboardHref}
                      onClick={onClose}
                      className="menu__auth-link menu__auth-link--primary"
                    >
                      {session.user.role === "admin"
                        ? isEnglish
                          ? "Admin panel"
                          : "Админка"
                        : isEnglish
                          ? "Dashboard"
                          : "Кабинет"}
                    </Link>
                    <button
                      type="button"
                      className="menu__auth-link menu__auth-link--outline"
                      onClick={() => {
                        signOut({ callbackUrl: getPathWithLocale("/", locale) });
                        onClose();
                      }}
                    >
                      {commonT("logout")}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="menu__auth-link menu__auth-link--primary"
                    onClick={() => {
                      onLogin();
                      onClose();
                    }}
                  >
                    {commonT("login")}
                  </button>
                )}
                <Link
                  href={getPathWithLocale("/book-call", locale)}
                  onClick={onClose}
                  className="menu__auth-link menu__auth-link--outline"
                >
                  {navbarT("bookCall")}
                </Link>
              </div>
            </div>
      </aside>
    </>
  );
}